import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getOrderDetail } from '../../services/ordersService';
import './OrderDetail.css';

const ORIGIN_STYLES = {
  'Яндекс.Еда': { bg: '#FFCC00', color: '#1a1a1a' },
  UzumTezkor:   { bg: '#7C3AED', color: '#fff' },
  Wolt:         { bg: '#00C2E8', color: '#0d0d0d' },
  'Web-panel':  { bg: '#f97316', color: '#fff' },
  'Посадка':    { bg: '#10b981', color: '#fff' },
};
const DEFAULT_ORIGIN = { bg: '#6b7280', color: '#fff' };

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatSum(val) {
  if (val == null) return '—';
  return Number(val).toLocaleString('ru-RU');
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isLoading: authLoading } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    getOrderDetail(orderId)
      .then(setOrder)
      .catch(() => setError('Не удалось загрузить заказ'))
      .finally(() => setLoading(false));
  }, [authLoading, orderId]);

  if (authLoading || loading) {
    return (
      <div className="od-page">
        <div className="od-loading">
          <div className="od-spinner" />
          <p>Загрузка заказа...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="od-page">
        <button className="od-back" onClick={() => navigate('/orders')}>← Назад</button>
        <div className="od-empty">
          <p>{error || 'Заказ не найден'}</p>
        </div>
      </div>
    );
  }

  const style = ORIGIN_STYLES[order.origin_name] || DEFAULT_ORIGIN;
  const hasDiscount = order.full_sum && order.result_sum && order.result_sum < order.full_sum;
  const activeItems = (order.items || []).filter(i => !i.deleted);

  return (
    <div className="od-page">
      <div className="od-topbar">
        <button className="od-back" onClick={() => navigate('/orders')}>← Заказы</button>
      </div>

      <div className="od-header">
        <div className="od-header-left">
          <h1 className="od-title">Заказ #{order.number || '—'}</h1>
          {order.external_order_number && (
            <span className="od-ext-num">Внешний: #{order.external_order_number}</span>
          )}
        </div>
        <div className="od-header-right">
          {order.origin_name && (
            <span className="od-badge" style={{ background: style.bg, color: style.color }}>
              {order.origin_name}
            </span>
          )}
          {order.is_banquet_order && <span className="od-tag-banquet">Банкет</span>}
          <span className={`od-status ${order.is_closed_on_ps ? 'od-status-closed' : 'od-status-active'}`}>
            {order.is_closed_on_ps ? 'Завершён' : 'Активный'}
          </span>
        </div>
      </div>

      <div className="od-cards">
        <div className="od-card">
          <div className="od-card-label">Филиал</div>
          <div className="od-card-value">{order.organization?.name || '—'}</div>
          {order.organization?.restaurantAddress && (
            <div className="od-card-sub">{order.organization.restaurantAddress}</div>
          )}
        </div>

        <div className="od-card">
          <div className="od-card-label">Официант / Кассир</div>
          <div className="od-card-value">{order.waiter_name || '—'}</div>
          {order.cashier_name && order.cashier_name !== order.waiter_name && (
            <div className="od-card-sub">Кассир: {order.cashier_name}</div>
          )}
        </div>

        <div className="od-card">
          <div className="od-card-label">Сумма</div>
          <div className="od-card-value od-sum-main">{formatSum(order.result_sum ?? order.full_sum)} сум</div>
          {hasDiscount && (
            <div className="od-card-sub od-sum-old">{formatSum(order.full_sum)} сум</div>
          )}
          {order.processed_payments_sum != null && (
            <div className="od-card-sub">Оплачено: {formatSum(order.processed_payments_sum)} сум</div>
          )}
        </div>

        <div className="od-card">
          <div className="od-card-label">Открыт</div>
          <div className="od-card-value">{formatDate(order.open_time)}</div>
          {order.bill_time && (
            <div className="od-card-sub">Закрыт: {formatDate(order.bill_time)}</div>
          )}
        </div>

        {order.table && (
          <div className="od-card">
            <div className="od-card-label">Стол</div>
            <div className="od-card-value">#{order.table.number || order.table.id}</div>
          </div>
        )}
      </div>

      <div className="od-section">
        <h2 className="od-section-title">Позиции заказа <span className="od-count">{activeItems.length}</span></h2>
        {activeItems.length === 0 ? (
          <p className="od-no-items">Позиции отсутствуют</p>
        ) : (
          <div className="od-items-wrap">
            <table className="od-items-table">
              <thead>
                <tr>
                  <th>Блюдо</th>
                  <th>Размер</th>
                  <th>Кол-во</th>
                  <th>Цена</th>
                  <th>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {activeItems.map(item => (
                  <tr key={item.id}>
                    <td className="od-item-name">{item.product?.name || item.name || '—'}</td>
                    <td className="od-item-size">{item.size || '—'}</td>
                    <td className="od-item-amount">{item.amount ?? '—'}</td>
                    <td className="od-item-price">{formatSum(item.price)} сум</td>
                    <td className="od-item-total">{formatSum((item.price || 0) * (item.amount || 1))} сум</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className="od-foot-label">Итого</td>
                  <td className="od-foot-total">{formatSum(order.result_sum ?? order.full_sum)} сум</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
